<script lang="ts">
  import {
    Button,
    Form,
    Input,
    OTPInput,
    type Language,
  } from '@bond/components'
  import { STRINGS } from '$lib/constants/strings'
  import { page } from '$app/stores'
  import SocialAuthButton from './SocialAuthButton.svelte'
  import { schema } from '../../routes/[lang]/(auth)/login/schema'
  import type { SocialCallbackState } from '$lib/types/socialCallbackState'
  import { utils } from '$lib/utils'
  import type { CityId } from '@bond/bruce-api/common/city'

  export let cityId: CityId
  export let form:
    | ({
        twoFactorAuthenticationRequired?: boolean
        twoFactorSetupRequired?: boolean
        qrCode?: string
      } & Record<string, unknown>)
    | null
  export let hideSocial: boolean | undefined = undefined
  export let hideSignup: boolean | undefined = undefined

  $: lang = $page.params.lang as Language
  $: localeRoute = utils.getRoute(lang)

  $: socialState = JSON.stringify({
    search: $page.url.search,
    context: 'signin',
    city: cityId,
  } satisfies SocialCallbackState)

  $: twoFactorAuthenticationRequired =
    form &&
    'twoFactorAuthenticationRequired' in form &&
    (form.twoFactorAuthenticationRequired as boolean)

  $: twoFactorSetupRequired =
    form &&
    'twoFactorSetupRequired' in form &&
    (form.twoFactorSetupRequired as boolean)

  $: qrCode = form && 'qrCode' in form && (form.qrCode as string)
</script>

<Form schema={schema(lang)} action={localeRoute(`/login${$page.url.search}`)}>
  <section class="login">
    <fieldset
      name="login-credentials"
      class="login-credentials"
      hidden={twoFactorAuthenticationRequired || twoFactorSetupRequired}
    >
      <Input
        label={STRINGS[lang].LOGIN_EMAIL}
        type="email"
        name="email"
        variant="outlined"
        rounded
        block
        autocomplete="email"
        autofocus
        required
      />
      <Input
        label={STRINGS[lang].LOGIN_PASSWORD}
        type="password"
        name="password"
        variant="outlined"
        rounded
        block
        autocomplete="current-password"
      />
    </fieldset>
    {#if twoFactorSetupRequired}
      <div class="qr-container">
        <p>{STRINGS[lang].LOGIN_2FA_SETUP_DESCRIPTION}</p>
        <h3>{STRINGS[lang].LOGIN_2FA_SETUP_TITLE}</h3>
        <p>{STRINGS[lang].LOGIN_2FA_SETUP_SCAN}</p>
        <img
          alt="QR Code"
          src={`data:image/png;base64,${qrCode}`}
          width="200"
          height="200"
        />
        <h3>{STRINGS[lang].LOGIN_2FA_SETUP_VERIFY}</h3>
      </div>
    {/if}
    {#if twoFactorAuthenticationRequired || twoFactorSetupRequired}
      <OTPInput
        label={STRINGS[lang].LOGIN_AUTHENTICATION_CODE}
        name="2fa_token"
        id="2fa_token"
      />
    {/if}
    <Button type="submit" rounded block>
      {STRINGS[lang].LOGIN_LOGIN}
    </Button>
  </section>
</Form>
{#if !hideSocial}
  <section class="social">
    <p>{STRINGS[lang].LOGIN_OR_WITH_SOCIAL}</p>
    <div class="buttons">
      <SocialAuthButton social="apple" context="signin" state={socialState} />
      <SocialAuthButton social="google" context="signin" state={socialState} />
    </div>
  </section>
{/if}
<section class="other-actions">
  {#if !hideSignup}
    <Button
      variant="text"
      href={localeRoute(`/signup${$page.url.search}`)}
      rounded
    >
      {STRINGS[lang].LOGIN_SIGNUP}
    </Button>
  {/if}
  <Button variant="text" href={localeRoute('/password/reset')} rounded>
    {STRINGS[lang].LOGIN_FORGOT_PASSWORD}
  </Button>
</section>

<style lang="scss">
  .login {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1em;

    .login-credentials {
      border: none;
      margin: 0;
      padding: 0;
      width: 100%;
    }
  }

  .social {
    p {
      font-size: 16px;
      text-align: center;
    }

    .buttons {
      display: flex;
      justify-content: center;
      gap: 1em;
    }
  }

  .other-actions {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1em;
    margin-top: 1em;
  }
</style>
